import React from "react";
import styled, { css } from "styled-components";
import { Icon } from "./Icon";
import { style } from "./style";

const teamsColors = {
  1: "#2a82ff",
  2: "#00c6ff",
  3: "#01d0c0",
  4: "#00d688",
  5: "#80d34a",
  6: "#d7e200",
  7: "#ffc001",
  8: "#e681ff",
  9: "#ff4c6c",
  10: "#b458fc",
};

const DEFAULT_COLOR = teamsColors["1"];

const AVATAR_COLORS = [
  teamsColors["1"],
  teamsColors["2"],
  teamsColors["3"],
  teamsColors["4"],
  teamsColors["5"],
  teamsColors["6"],
  teamsColors["7"],
  teamsColors["8"],
  teamsColors["9"],
  teamsColors["10"],
];

const MAX_INITIAL_LENGTH = 2;
const DEFAULT_AVATAR_SIZE = "medium";
const ALLOWED_ICON_SIZES = ["medium", "contextTransition"];

const AVATAR_SIZES = {
  xsmall: 16,
  small: 24,
  medium: 32,
  large: 48,
  xlarge: 80,
  // this matches what currently exists in ember
  // we can remove this and use the medium size
  // once it is removed from the ember stack
  // This size is used for the SwitchAccountTransition component
  contextTransition: 53,
};

type AvatarSize = keyof typeof AVATAR_SIZES;
type AvatarColor = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | number;

const AvatarContainer = styled.div<{ size: AvatarSize }>`
  flex: 0 0 auto;
  position: relative;
  font-size: ${style.vars.fontSize.small};
  background-clip: padding-box;
  background-color: ${style.colors.grey.light};
  border-radius: 50%;

  ${({ size }) =>
    css`
      width: ${AVATAR_SIZES[size]}px;
      height: ${AVATAR_SIZES[size]}px;
    `}
`;

const AvatarInitials = styled.div<{
  avatarColor: AvatarColor;
  size?: AvatarSize;
}>`
  display: flex;
  justify-content: center;
  background-color: ${({ avatarColor }) => AVATAR_COLORS[avatarColor - 1] ?? DEFAULT_COLOR};
  color: ${style.colors.grey.darkest};
  align-items: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  font-size: 7px;
  text-transform: uppercase;
  overflow: hidden;
  background-clip: padding-box;

  ${({ size }) =>
    size === "small" &&
    css`
      font-size: ${style.vars.fontSize.xsmall};
    `}

  ${({ size }) =>
    size === "medium" &&
    css`
      font-size: ${style.vars.fontSize.xsmall};
    `}

  ${({ size }) =>
    (size === "large" || size === "contextTransition") &&
    css`
      font-size: 18px;
    `}
`;

const AvatarImage = styled.img<{ size: AvatarSize; isTeam: boolean }>`
  overflow: hidden;
  border-radius: 50%;
  box-shadow: ${({ size, isTeam }) => (size === "xsmall" || isTeam ? `0 0 0 2px ${style.colors.white}` : "none")};

  ${({ isTeam }) =>
    isTeam &&
    css`
      position: absolute;
      right: -50%;
      transform: translateX(-53%);
      bottom: -0.15rem;
      width: ${AVATAR_SIZES.xsmall}px;
      height: ${AVATAR_SIZES.xsmall}px;
    `}

  ${({ size, isTeam }) =>
    isTeam &&
    size === "contextTransition" &&
    css`
      width: 24px;
      height: 24px;
      right: -22px;
      transform: translateX(-50%);
      bottom: -0.1rem;
    `}
`;

const SecureSignInWarningIcon = styled(Icon)<{ size: AvatarSize }>`
  position: absolute;
  right: -50%;
  transform: translateX(-53%);
  bottom: -0.15rem;
  ${({ size }) =>
    css`
      width: ${AVATAR_SIZES[size] / 2}px;
      height: ${AVATAR_SIZES[size] / 2}px;
    `}
`;

export type AvatarProps = {
  warning?: boolean;
  team?: {
    initials: string;
    color?: AvatarColor;
  };
  size?: AvatarSize;
  src?: string;
};

export const Avatar: React.FC<AvatarProps> = (props) => {
  const {
    team,
    src,
    size = DEFAULT_AVATAR_SIZE,
    warning = false, // eslint-disable-line @typescript-eslint/naming-convention
  } = props;

  /**
   * The large icon can either be the initials of the team, or the image of the user.
   * If a `team` is provided, then display the team's initials. Otherwise, if a user `src`
   * is provided then display the user's image. If both are missing, the large icon will
   * be nothing and the avatar will appear as a grey circle.
   */
  const largeIcon = React.useMemo(() => {
    if (team) {
      const { color = 0, initials } = team;

      return (
        <AvatarInitials avatarColor={color} size={size}>
          {initials.slice(0, MAX_INITIAL_LENGTH)}
        </AvatarInitials>
      );
    }

    if (src) {
      return <AvatarImage isTeam={false} size={size} src={src} alt="Avatar" />;
    }

    return null;
  }, [team?.color, team?.initials, src, size]);

  /**
   * The small icon only displays when the avatar size is in the `ALLOWED_ICON_SIZES`.
   * Additionally, if there is a `warning` it will display the warning icon.
   * Otherwise, if there is a `team` display the user's image.
   */
  const smallIcon = React.useMemo(() => {
    if (ALLOWED_ICON_SIZES.includes(size) === false) {
      return null;
    }

    if (warning) {
      return <SecureSignInWarningIcon icon="alert-warning-white-border" size={size} />;
    }

    if (team && src) {
      return <AvatarImage isTeam={true} src={src} size={size} alt="Avatar" />;
    }
  }, [team?.color, team?.initials, src, size, warning]);

  return (
    <AvatarContainer size={size}>
      {largeIcon}
      {smallIcon}
    </AvatarContainer>
  );
};
